/* "Let's move on to my offer" — did she ask to go to another module?
 *
 * Lifted out of ChatPanel.jsx (2026-09-24) so it can be tested, after it fired
 * on her during a demo. Her answer to My Vision's first question was
 *
 *   "…whether I'm with my kids or my clients. My days have a mix of structure
 *    and open space…"
 *
 * and a "Want to go to My Client? Take me there" card appeared under it. The
 * old test needed an intent word ANYWHERE and a module name ANYWHERE: "open"
 * (from "open space") was the intent, and "my client" (inside "my clients")
 * was the name. Two unrelated words, a sentence apart, made a navigation
 * request she never made — mid-section, on top of the lock she was asking for.
 *
 * NOW THE NAME HAS TO FOLLOW THE VERB, close by, as a whole name: "move on to
 * my offer", "take me to My Market", "let's go to offer ideation". A module
 * name mentioned in passing, or as part of a longer word ("my clients"), is not
 * a request to go there.
 *
 * Still deliberately a SUGGESTION: the card offers the door and never moves
 * her (ChatPanel). A miss costs her a click on the nav; a false hit put a
 * second decision on screen in the middle of the one she was making.
 */

/* The verbs that mean "take me somewhere". "open" and "next" are gone: "open
   space", "open to my story changing", "next week" are ordinary sentences, and
   "next" without a place is the next SECTION, which the next-section button
   answers. "start" stays — "let's start My Offer" is a real request, and with
   the name required right after it, "start with you" cannot match. */
const VERBS = "(?:go|going|move|moving|switch|jump|head|take me|skip|start|starting|onto|on to)";

/* How far the name may sit after the verb: "move on to" + "the" + a name is a
   handful of words, never a sentence. */
const GAP = "(?:\\s+(?:on|over|back|straight|right|now|then|to|into|onto|the|my|over to|on to))*\\s+";

const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * The module she asked to go to, or null.
 * @param text     what she typed
 * @param modules  { [id]: { label } } — MODULE_META
 * @param current  the module she is in, never offered back to her
 */
export function moduleFromText(text, modules = {}, current = null) {
  const t = String(text || "");
  for (const [id, meta] of Object.entries(modules)) {
    if (id === current || !meta?.label) continue;
    /* The label, optionally without its leading "My" ("go to offer" is not a
       request, but "go to My Offer" and "move on to offer ideation" are). Whole
       words only: "my clients" is not "My Client". */
    const label = escape(meta.label.toLowerCase());
    const bare = label.replace(/^my\\?\s+/, "");
    const names = bare !== label && bare.split(/\s+/).length > 1 ? `(?:${label}|${bare})` : label;
    const re = new RegExp(`\\b${VERBS}${GAP}${names}(?![a-z])`, "i");
    if (re.test(t)) return id;
  }
  return null;
}
